import React, { useMemo, useState } from "react";
import { useLaboBall } from "./LaboBall";
import { filterByDateRange, frequencyRanking, minusDays, type LaboDraw } from "./laboStats";
import styles from "./labo.module.css";

interface Props {
  tiragesDesc: LaboDraw[];
  /** Les tirages sont l'exemple figé de la page publique (premiers tirages de la formule). */
  exemple?: boolean;
  /** Le serveur n'a servi qu'une fenêtre des derniers tirages (compte gratuit, `access.truncated`). */
  fenetreLimitee?: boolean;
}

type Preset = "7t" | "30" | "90" | "365" | "all";
const PRESETS: { key: Preset; label: string }[] = [
  { key: "7t", label: "7 tirages" },
  { key: "30", label: "30 jours" },
  { key: "90", label: "90 jours" },
  { key: "365", label: "1 an" },
  { key: "all", label: "Tout" },
];

/*
 * Sur une cinquantaine de tirages (exemple figé, fenêtre du compte gratuit), 90 jours et 1 an
 * donnent le même résultat que « Tout » : seuls les raccourcis qui changent le classement restent.
 */
const PRESETS_COURTS: Preset[] = ["7t", "30", "all"];

const TOP_SIZES = [4, 6, 10, 20];

/** Seuil de l'étoile : sorti au moins 3 fois sur les 7 derniers tirages. */
const STAR_THRESHOLD = 3;

const iso = (d: string) => String(d).slice(0, 10);

function frenchDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Outil « Top numéros sur une période » : le joueur choisit deux bornes de
 * dates (ou un preset) et voit les numéros les plus sortis sur cette période,
 * classés par nombre de sorties. Calcul côté client depuis l'historique.
 */
export const TopNumerosPeriodeTool: React.FC<Props> = ({ tiragesDesc, exemple = false, fenetreLimitee = false }) => {
  const Ball = useLaboBall();
  const dataMax = tiragesDesc.length ? iso(tiragesDesc[0].dateTirage) : "";
  const dataMin = tiragesDesc.length ? iso(tiragesDesc[tiragesDesc.length - 1].dateTirage) : "";

  // Exemple figé : tout l'exemple annoncé dans l'en-tête de la page (même vue que le serveur).
  const [preset, setPreset] = useState<Preset>(exemple ? "all" : "30");
  const [topN, setTopN] = useState(10);
  // Début d'une période en jours, jamais avant le premier tirage servi : le verdict ne cite
  // que des dates couvertes par les tirages reçus.
  const debutJours = (jours: number) => {
    const debut = minusDays(dataMax, jours);
    return debut < dataMin ? dataMin : debut;
  };
  const [start, setStart] = useState<string>(() => (!dataMax ? "" : exemple ? dataMin : debutJours(30)));
  const [end, setEnd] = useState<string>(dataMax);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (!dataMax) return;
    setEnd(dataMax);
    if (p === "all") {
      setStart(dataMin);
    } else if (p === "7t") {
      // Borne basse = date du 7e tirage le plus récent (compte de tirages, pas de jours).
      const idx = Math.min(6, tiragesDesc.length - 1);
      setStart(idx >= 0 ? iso(tiragesDesc[idx].dateTirage) : dataMin);
    } else {
      setStart(debutJours(Number(p)));
    }
  };

  const isStarPreset = preset === "7t";
  const presets = exemple || fenetreLimitee ? PRESETS.filter((p) => PRESETS_COURTS.includes(p.key)) : PRESETS;

  // Édition manuelle des dates → bascule en mode « personnalisé ».
  const onStart = (v: string) => { setStart(v); setPreset("all"); };
  const onEnd = (v: string) => { setEnd(v); setPreset("all"); };

  const { ranking, drawCount } = useMemo(() => {
    const window = filterByDateRange(tiragesDesc, start || undefined, end || undefined);
    return { ranking: frequencyRanking(window), drawCount: window.length };
  }, [tiragesDesc, start, end]);

  const top = useMemo(() => ranking.filter((r) => r.count > 0).slice(0, topN), [ranking, topN]);
  const maxCount = top.length ? top[0].count : 0;

  return (
    <div className={styles.card}>
      <div className={styles.verdict}>
        <p className={styles.verdictSentence}>
          {top.length > 0 ? (
            <>
              Du <span className={styles.verdictAccent}>{frenchDate(start)}</span> au{" "}
              <span className={styles.verdictAccent}>{frenchDate(end)}</span>, le numéro{" "}
              <span className={styles.verdictAccent}>{top[0].number}</span> est le plus sorti :{" "}
              <span className={styles.verdictAccent}>{top[0].count} fois</span>.
            </>
          ) : (
            "Aucun tirage sur la période sélectionnée."
          )}
        </p>
        <div className={styles.verdictPills}>
          <span className={`${styles.pill} ${styles.pillNeutral}`}>
            {drawCount} tirage{drawCount > 1 ? "s" : ""} sur la période
          </span>
        </div>
      </div>

      <div className={styles.controlsRow}>
        <span className={styles.controlLabel}>Période</span>
        <div className={styles.segmented} role="group" aria-label="Période">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`${styles.segmentedBtn} ${preset === p.key ? styles.segmentedBtnActive : ""}`}
              aria-pressed={preset === p.key}
              onClick={() => applyPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.controlsRow}>
        <label className={styles.controlLabel}>
          Du{" "}
          <input
            type="date"
            value={start}
            min={dataMin}
            max={end || dataMax}
            onChange={(e) => onStart(e.target.value)}
            className={styles.dateInput}
          />
        </label>
        <label className={styles.controlLabel}>
          au{" "}
          <input
            type="date"
            value={end}
            min={start || dataMin}
            max={dataMax}
            onChange={(e) => onEnd(e.target.value)}
            className={styles.dateInput}
          />
        </label>
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>Top</span>
          <div className={styles.segmented} role="group" aria-label="Nombre de numéros">
            {TOP_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                className={`${styles.segmentedBtn} ${topN === s ? styles.segmentedBtnActive : ""}`}
                aria-pressed={topN === s}
                onClick={() => setTopN(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {top.length > 0 && (
        <ol className={`${styles.rankList} ${styles.rankListPeriode}`}>
          {top.map((r, i) => {
            const isStar = isStarPreset && r.count >= STAR_THRESHOLD;
            return (
              <li key={r.number} className={styles.rankItem}>
                <span className={styles.rankPos}>{i + 1}</span>
                <Ball number={r.number} star={isStar} />
                <div className={styles.rankBarTrack}>
                  <div
                    className={styles.rankBarFill}
                    style={{ width: `${maxCount ? (r.count / maxCount) * 100 : 0}%` }}
                  />
                </div>
                <span className={styles.rankCount}>
                  {isStar && <span aria-hidden style={{ color: "var(--mag-gold-dark)" }}>★ </span>}
                  {r.count} sortie{r.count > 1 ? "s" : ""}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {isStarPreset && top.some((r) => r.count >= STAR_THRESHOLD) && (
        <p className={styles.sectionHint} style={{ marginTop: ".5rem" }}>
          <span style={{ color: "var(--mag-gold-dark)" }} aria-hidden="true">★</span> Sorti au moins{" "}
          {STAR_THRESHOLD} fois sur les 7 derniers tirages (observation, sans effet sur les tirages suivants).
        </p>
      )}

      <p className={styles.methodNote}>
        Le classement compte, sur la période choisie, combien de fois chaque numéro a été tiré. Ces
        fréquences sont des observations historiques : chaque tirage Keno est indépendant, elles
        n'indiquent pas les numéros à venir.
      </p>
    </div>
  );
};
